/**
 * Market configuration for GVGmall country/language/currency selector.
 *
 * Requirements:
 * - GVGmall default must be USD, never BRL.
 * - UK (GB) does not have GBP prices in this system, so it falls back to USD.
 * - EUR countries use EUR prices.
 * - Brazil uses BRL only when explicitly selected.
 */

export type SupportedLanguage = 'pt' | 'en' | 'es' | 'fr' | 'de' | 'it'
export type SupportedCurrency = 'USD' | 'EUR' | 'BRL' | 'GBP'
export type SupportedCurrencyLower = 'usd' | 'eur' | 'brl' | 'gbp'
export type MarketCode = 'US' | 'BR' | 'ES' | 'FR' | 'DE' | 'GB' | 'AUTO'

export interface MarketConfig {
  code: MarketCode
  country: string
  countryCode: string
  language: SupportedLanguage
  locale: string
  currency: SupportedCurrency
  currencyLower: SupportedCurrencyLower
  flag: string
  label: string
  languageLabel: string
  currencyLabel: string
}

export const MARKETS: MarketConfig[] = [
  {
    code: 'US',
    country: 'United States',
    countryCode: 'US',
    language: 'en',
    locale: 'en-US',
    currency: 'USD',
    currencyLower: 'usd',
    flag: '🇺🇸',
    label: 'United States',
    languageLabel: 'English',
    currencyLabel: 'US Dollar',
  },
  {
    code: 'BR',
    country: 'Brazil',
    countryCode: 'BR',
    language: 'pt',
    locale: 'pt-BR',
    currency: 'BRL',
    currencyLower: 'brl',
    flag: '🇧🇷',
    label: 'Brazil',
    languageLabel: 'Português',
    currencyLabel: 'Brazilian Real',
  },
  {
    code: 'ES',
    country: 'Spain',
    countryCode: 'ES',
    language: 'es',
    locale: 'es-ES',
    currency: 'EUR',
    currencyLower: 'eur',
    flag: '🇪🇸',
    label: 'Spain',
    languageLabel: 'Español',
    currencyLabel: 'Euro',
  },
  {
    code: 'FR',
    country: 'France',
    countryCode: 'FR',
    language: 'fr',
    locale: 'fr-FR',
    currency: 'EUR',
    currencyLower: 'eur',
    flag: '🇫🇷',
    label: 'France',
    languageLabel: 'Français',
    currencyLabel: 'Euro',
  },
  {
    code: 'DE',
    country: 'Germany',
    countryCode: 'DE',
    language: 'de',
    locale: 'de-DE',
    currency: 'EUR',
    currencyLower: 'eur',
    flag: '🇩🇪',
    label: 'Germany',
    languageLabel: 'Deutsch',
    currencyLabel: 'Euro',
  },
  {
    code: 'GB',
    country: 'United Kingdom',
    countryCode: 'GB',
    language: 'en',
    locale: 'en-GB',
    currency: 'GBP',
    currencyLower: 'gbp',
    flag: '🇬🇧',
    label: 'United Kingdom',
    languageLabel: 'English',
    currencyLabel: 'British Pound',
  },
]

export const MARKET_BY_CODE: Record<string, MarketConfig> = MARKETS.reduce(
  (acc, market) => {
    acc[market.code] = market
    return acc
  },
  {} as Record<string, MarketConfig>
)

export const DEFAULT_MARKET: MarketConfig = MARKETS.find((m) => m.code === 'US')!

export const DEFAULT_CURRENCY: SupportedCurrency = 'USD'
export const DEFAULT_CURRENCY_LOWER: SupportedCurrencyLower = 'usd'

export function getMarketByCode(code: string): MarketConfig {
  const normalized = String(code || '').trim().toUpperCase()
  if (normalized === 'AUTO' || !normalized) return DEFAULT_MARKET
  return MARKET_BY_CODE[normalized] || DEFAULT_MARKET
}

export function getMarketByLanguage(language: string): MarketConfig {
  const normalized = String(language || '').trim().toLowerCase()
  const found = MARKETS.find((m) => m.language === (normalized as SupportedLanguage))
  return found || DEFAULT_MARKET
}

export function normalizeCurrency(input: unknown): SupportedCurrencyLower {
  const c = String(input || '').trim().toLowerCase()
  if (c === 'usd') return 'usd'
  if (c === 'eur') return 'eur'
  if (c === 'brl') return 'brl'
  if (c === 'gbp') return 'gbp'
  return DEFAULT_CURRENCY_LOWER
}

export function normalizeLanguage(input: unknown): SupportedLanguage {
  const l = String(input || '').trim().toLowerCase()
  if (l === 'pt' || l.startsWith('pt')) return 'pt'
  if (l === 'en' || l.startsWith('en')) return 'en'
  if (l === 'es' || l.startsWith('es')) return 'es'
  if (l === 'fr' || l.startsWith('fr')) return 'fr'
  if (l === 'de' || l.startsWith('de')) return 'de'
  if (l === 'it' || l.startsWith('it')) return 'it'
  return 'en'
}
